import { privateApi } from '../config/api';

class QuickSwapService {
  constructor() {
    this.baseURL = '/v1/quick-swap';
  }

  /**
   * Calculate swap amounts without executing the transaction
   * @param {string} fromCurrency - Source currency code
   * @param {number} amount - Amount to swap
   * @param {number} btc1Price - Current BTC1 price
   * @returns {Promise<Object>} Calculated swap details
   */
  async calculateSwapAmounts(fromCurrency, amount, btc1Price) {
    try {
      const response = await privateApi.get(`${this.baseURL}/`, {
        params: {
          from_currency: fromCurrency,
          amount: amount,
          btc1_price: btc1Price
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating swap amounts:', error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Execute a quick swap from wallet currency to BTC1
   * @param {string} fromCurrency - Source currency code
   * @param {number} amount - Amount to swap
   * @param {number} btc1Price - Current BTC1 price
   * @returns {Promise<Object>} Swap execution result
   */
  async executeQuickSwap(fromCurrency, amount, btc1Price) {
    try {
      const response = await privateApi.post(`${this.baseURL}/`, {
        from_currency: fromCurrency,
        amount: amount,
        btc1_price: btc1Price
      });
      return response.data;
    } catch (error) {
      console.error('Error executing quick swap:', error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Get available currencies for quick swap
   * @returns {Promise<Array>} List of available currencies with balances
   */
  async getAvailableCurrencies() {
    try {
      const response = await privateApi.get(`${this.baseURL}/currencies/`);
      return response.data.currencies;
    } catch (error) {
      console.error('Error getting available currencies:', error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Get current BTC1 price
   * @returns {Promise<number>} Current BTC1 price in USD
   */
  async getBTC1Price() {
    try {
      // Use existing orbitxService to get BTC1 price
      const orbitxService = (await import('./orbitxService')).default;
      const coinsData = await orbitxService.getAvailableCoins();
      
      // Get BTC1 price from coins data
      const btc1CoinData = coinsData.coins?.BTC1;
      if (btc1CoinData?.price !== null && btc1CoinData?.price !== undefined) {
        return btc1CoinData.price;
      }
      
      // Fallback to pairs data
      const btc1UsdtPair = coinsData.pairs_data?.['BTC1-USDT'];
      if (btc1UsdtPair?.price) {
        return btc1UsdtPair.price;
      }
      
      // Final fallback
      return 108;
    } catch (error) {
      console.error('Error getting BTC1 price:', error);
      return 108; // Fallback price
    }
  }
}

export default new QuickSwapService();
